import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Platform } from 'react-native';
import { Feather, Octicons } from '@expo/vector-icons';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import * as LocalAuthentication from 'expo-local-authentication';

import { B, P, SubCaption } from '../../components/Typography';
import styled from 'styled-components/native';
import Button from '../../components/Button';
import { useLogout } from '../../hooks/user/useLogout';
import { useUserContext } from '../../context/UserContext';
import { useAsyncStorage } from '@react-native-async-storage/async-storage';
import { useUpdateUser } from '../../hooks/user/useUpdateUser';
import { HealthKitSync } from '../../tools/health-kit';
import { H5 } from '../../components/Typography';
import Spacer from '../../components/Spacer';
import { Caption } from '../../components/Typography';
import { format, parse } from 'date-fns';
import biometrics from '../../biometrics/biometrics';
import AppManifest from '../../common/AppManifest';
import ProfilePhoto from '../../components/ProfilePhoto';
import { log } from '../../tools/LogHelper';
import { stringify } from 'flatted';
import elementIDs from '../../../elementIDs';
import CustomToggleSwitch from '../../components/CustomToggleSwitch';
import { GoogleHealthConnect } from '../../tools/google-health-connect';

const StyledScrollView = styled.ScrollView`
  background-color: #fdfbf7;
  padding: 16px;
  flex: 1;
`;

const StyledContainer = styled.View`
  width: 100%;
  align-self: center;
  align-items: center;
  max-width: 768px;
  margin-top: ${Platform.OS === 'web' ? 0 : 10}px;
`;

const StyledButton = styled(Button)`
  width: 100%;
`;

const StyledProfileInfoContainer = styled.TouchableOpacity`
  flex-direction: row;
  background-color: white;
  width: 100%;
  border-radius: 16px;
  border-width: 2px;
  border-color: #cfcaf6;
  align-items: center;
  padding: 17px 33px 17px 16px;
`;

const StyledProfileInfoView = styled.View`
  flex: 1;
`;

const StyledVersionBuildText = styled(P)`
  margin-top: 32px;
  font-size: 10px;
  color: #333;
  align-self: center;
`;

const StyledTextNotifContainer = styled.View`
  flex-direction: row;
  align-items: center;
`;

const StyledIcon = styled(Feather)`
  margin-right: 4px;
`;

const StyledCustomSwitch = styled(CustomToggleSwitch)``;

const StyledNotificationsContainer = styled.View`
  flex-direction: row;
  min-width: 200px;
  width: 100%;
  align-items: center;
  justify-content: space-between;
`;

const phoneRegExp =
  /^((\+\d{1,3}(-| )?\(?\d\)?(-| )?\d{1,3})|(\(?\d{2,3}\)?))(-| )?(\d{3,4})(-| )?(\d{4})(( x| ext)\d{1,5}){0,1}$/;

const MyProfileSettingsScreen: React.FC = () => {
  const { user, setUser, updateUserSession } = useUserContext();
  const [biometricLoginEnabled, setBiometricLoginEnabled] = useState(false);
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const navigation = useNavigation<StackNavigationProp<any>>();
  const { logout } = useLogout();
  const { getItem: getIsGoogleFitAuthorized } = useAsyncStorage(
    'google-fit-connected'
  );
  const [healthKitAuthorized, setHealthKitAuthorized] =
    useState<boolean>(false);
  const [googleFitAuthorized, setGoogleFitAuthorized] =
    useState<boolean>(false);
  const [healthConnectAuthorized, setHealthConnectAuthorized] =
    useState<boolean>(false);
  const { updatedUser, clearUpdatedUser, updateUser } = useUpdateUser();

  const isPhoneNumberValid = useMemo(() => {
    const phoneNumber = user?.phoneNumber || '';
    return Boolean(phoneNumber.match(phoneRegExp));
  }, [user?.phoneNumber]);

  useFocusEffect(
    useCallback(() => {
      updateUserSession();
      if (Platform.OS === 'ios') {
        const getHealthKitAuthStatus = async () => {
          const healthkitAuth = new HealthKitSync(user?.id || 0);
          healthkitAuth.getAuthStatus().then(result => {
            if (result) {
              setHealthKitAuthorized(true);
            }
          });
        };
        getHealthKitAuthStatus().catch(err => {
          log(stringify(err));
        });
      } else if (Platform.OS === 'android') {
        getIsGoogleFitAuthorized().then(value => {
          if (value) {
            setGoogleFitAuthorized(true);
          }
        });
        (async () => {
          const healthConnect = new GoogleHealthConnect(user?.id || 0);
          const initialized = await healthConnect.authorize();
          if (initialized) {
            const permissions = await healthConnect.getGrantedPermissions();
            if (permissions.length) {
              setHealthConnectAuthorized(true);
            }
          }
        })();
      }
    }, [updateUserSession])
  );

  function handleLogOut() {
    logout();
  }

  useEffect(() => {
    if (updatedUser) {
      clearUpdatedUser();
      setUser(updatedUser);
    }
  }, [clearUpdatedUser, setUser, updatedUser]);

  const verifyEnrolledBiometric = async () => {
    const hasBiometricHardware = await LocalAuthentication.hasHardwareAsync();
    setBiometricAvailable(hasBiometricHardware);
  };
  const getBiometricLoginStatus = async () => {
    const enabled = await biometrics.getBiometricLogin();
    setBiometricLoginEnabled(enabled === 'true');
  };

  useFocusEffect(() => {
    if (Platform.OS !== 'web') {
      verifyEnrolledBiometric();
      getBiometricLoginStatus();
    }
  });

  const connectedWearables = useMemo(() => {
    const wearables: string[] = [];
    if (user?.ouraAccessToken) {
      wearables.push('Oura');
    }
    if (user?.fitbitAccessToken) {
      wearables.push('Fitbit');
    }
    if (user?.garminConfig) {
      wearables.push('Garmin');
    }
    if (healthKitAuthorized) {
      wearables.push('Apple Health');
    }
    if (googleFitAuthorized) {
      wearables.push('Google Fit');
    }
    if (healthConnectAuthorized) {
      wearables.push('Health Connect');
    }
    return wearables.join(', ');
  }, [googleFitAuthorized, healthKitAuthorized, healthConnectAuthorized, user]);
  return (
    <StyledScrollView
      onMomentumScrollEnd={event => {
        const scrollOffset = event.nativeEvent.contentOffset.y;
        setScrollPosition(scrollOffset);
      }}
      scrollIndicatorInsets={{ right: 1 }}
      contentContainerStyle={{
        flexGrow: 1,
        alignItems: 'center',
        paddingBottom: 40
      }}
    >
      <StyledContainer>
        <ProfilePhoto />
        <Spacer size={3} />
        <H5 testID={elementIDs.settingsNameLabelID} color='dark'>
          {user?.firstName} {user?.lastName}
        </H5>
        <Spacer size={1} />
        <P testID={elementIDs.settingsEmailLabelID} color='dark'>
          {user?.email}
        </P>
        <Spacer size={5} />
        <StyledProfileInfoContainer
          onPress={() => {
            navigation.navigate('MyProfileEditInfo', {
              profileInfoType: 'name'
            });
          }}
        >
          <StyledProfileInfoView testID={elementIDs.settingsNameID}>
            <B color='dark'>Name</B>
            <Spacer size={1} />
            <Caption color='dark'>
              {user?.firstName} {user?.lastName}
            </Caption>
          </StyledProfileInfoView>
          <Octicons name='chevron-right' size={24} color='black' />
        </StyledProfileInfoContainer>
        <Spacer size={1.5} />
        <StyledProfileInfoContainer
          onPress={() => {
            navigation.navigate('MyProfileEditInfo', {
              profileInfoType: 'email'
            });
          }}
        >
          <StyledProfileInfoView testID={elementIDs.settingsEmailID}>
            <B color='dark'>Email</B>
            <Spacer size={1} />
            <Caption color='dark'>{user?.email}</Caption>
          </StyledProfileInfoView>
          <Octicons name='chevron-right' size={24} color='black' />
        </StyledProfileInfoContainer>
        <Spacer size={1.5} />
        <StyledNotificationsContainer>
          <SubCaption color='dark'>
            Enable notifications for participants activities
          </SubCaption>
          <StyledCustomSwitch
            value={user?.notifyForParticipantsActivities ?? false}
            onToggle={async () => {
              await updateUser({
                notifyForParticipantsActivities:
                  !user?.notifyForParticipantsActivities
              });
            }}
          />
        </StyledNotificationsContainer>
        <Spacer size={1.5} />
        <StyledProfileInfoContainer
          onPress={() => {
            navigation.navigate('MyProfileEditInfo', {
              profileInfoType: 'password'
            });
          }}
        >
          <StyledProfileInfoView testID={elementIDs.settingsPasswordID}>
            <B color='dark'>Password</B>
            <Spacer size={1} />
            <Caption color='dark'>••••••••</Caption>
          </StyledProfileInfoView>
          <Octicons name='chevron-right' size={24} color='black' />
        </StyledProfileInfoContainer>
        <Spacer size={1.5} />
        <StyledProfileInfoContainer
          onPress={() => {
            navigation.navigate('MyProfileEditInfo', {
              profileInfoType: 'birthDate'
            });
          }}
        >
          <StyledProfileInfoView testID={elementIDs.settingsBirthdayID}>
            <B color='dark'>Date of Birth</B>
            <Spacer size={1} />
            <Caption color='dark'>
              {user?.birthDate
                ? format(
                    parse(user.birthDate, 'yyyy-MM-dd', new Date()),
                    'MM/dd/yyyy'
                  )
                : ''}
            </Caption>
          </StyledProfileInfoView>
          <Octicons name='chevron-right' size={24} color='black' />
        </StyledProfileInfoContainer>
        <Spacer size={1.5} />
        <StyledProfileInfoContainer
          onPress={() => {
            navigation.navigate('MyProfileEditInfo', {
              profileInfoType: 'state'
            });
          }}
        >
          <StyledProfileInfoView testID={elementIDs.settingsStateID}>
            <B color='dark'>State of Residence</B>
            <Spacer size={1} />
            <Caption color='dark'>{user?.state}</Caption>
          </StyledProfileInfoView>
          <Octicons name='chevron-right' size={24} color='black' />
        </StyledProfileInfoContainer>
        <Spacer size={1.5} />
        <StyledProfileInfoContainer
          onPress={() => {
            navigation.navigate('MyProfileEditInfo', {
              profileInfoType: 'address'
            });
          }}
        >
          <StyledProfileInfoView>
            <B color='dark'>Address</B>
            <Spacer size={1} />
            <Caption color='dark'>
              {user?.address?.line1
                ? [
                    user.address.line1,
                    user.address.line2,
                    [user.address.city, user.address.state]
                      .filter(Boolean)
                      .join(', '),
                    user.address.postalCode
                  ]
                    .filter(Boolean)
                    .join(', ')
                : ''}
            </Caption>
          </StyledProfileInfoView>
          <Octicons name='chevron-right' size={24} color='black' />
        </StyledProfileInfoContainer>
        <Spacer size={1.5} />
        <StyledProfileInfoContainer
          onPress={() => {
            navigation.navigate('MyProfileEditInfo', {
              profileInfoType: 'phoneNumber'
            });
          }}
        >
          <>
            <StyledProfileInfoView testID={elementIDs.settingsPhoneID}>
              <B color='dark'>Phone Number</B>
              <Spacer size={1} />
              <Caption color='dark'>{user?.phoneNumber || ''}</Caption>
              {isPhoneNumberValid && (
                <>
                  <Spacer size={1} />
                  <StyledTextNotifContainer>
                    <StyledIcon
                      name={user?.notifyForMessages ? 'check-square' : 'square'}
                      size={20}
                      color='black'
                    />
                    <Caption color='dark'>Text for messages</Caption>
                  </StyledTextNotifContainer>
                  <Spacer size={1} />
                  <StyledTextNotifContainer>
                    <StyledIcon
                      name={user?.notifyForStudy ? 'check-square' : 'square'}
                      size={20}
                      color='black'
                    />
                    <Caption color='dark'>Text for study notifications</Caption>
                  </StyledTextNotifContainer>
                </>
              )}
            </StyledProfileInfoView>
          </>
          <Octicons name='chevron-right' size={24} color='black' />
        </StyledProfileInfoContainer>
        <Spacer size={1.5} />
        <StyledProfileInfoContainer
          onPress={() => {
            navigation.navigate('MyProfileEditInfo', {
              profileInfoType: 'unitSystem'
            });
          }}
        >
          <StyledProfileInfoView testID={elementIDs.settingsUnitSystemID}>
            <B color='dark'>Unit System</B>
            <Spacer size={1} />
            <Caption color='dark'>{user?.unitSystem}</Caption>
          </StyledProfileInfoView>
          <Octicons name='chevron-right' size={24} color='black' />
        </StyledProfileInfoContainer>
        <Spacer size={1.5} />
        <StyledProfileInfoContainer
          onPress={() => {
            navigation.navigate('MyProfileEditInfo', {
              profileInfoType: 'biologicalSex'
            });
          }}
        >
          <StyledProfileInfoView testID={elementIDs.settingsBiologicalSexID}>
            <B color='dark'>Biological Sex</B>
            <Spacer size={1} />
            <Caption color='dark'>{user?.biologicalSex}</Caption>
          </StyledProfileInfoView>
          <Octicons name='chevron-right' size={24} color='black' />
        </StyledProfileInfoContainer>
        {biometricAvailable && (
          <>
            <Spacer size={1.5} />
            <StyledProfileInfoContainer
              onPress={() => {
                navigation.navigate('MyProfileBiometricSettings');
              }}
            >
              <StyledProfileInfoView>
                <B color='dark'>
                  {Platform.OS === 'ios'
                    ? 'Face ID / Touch ID'
                    : 'Face / Fingerprint Biometric'}
                </B>
                <Spacer size={1} />
                <Caption color='dark'>
                  {biometricLoginEnabled ? 'Enabled' : 'Disabled'}
                </Caption>
              </StyledProfileInfoView>
              <Octicons name='chevron-right' size={24} color='black' />
            </StyledProfileInfoContainer>
          </>
        )}
        <Spacer size={1.5} />
        <StyledProfileInfoContainer
          onPress={() => {
            navigation.navigate('MyProfileWearableSettings');
          }}
        >
          <StyledProfileInfoView testID={elementIDs.settingsWearableID}>
            <B color='dark'>Wearable Integrations</B>
            <Spacer size={1} />
            <Caption color='dark'>{connectedWearables}</Caption>
          </StyledProfileInfoView>
          <Octicons name='chevron-right' size={24} color='black' />
        </StyledProfileInfoContainer>
        <Spacer size={1.5} />
        <StyledProfileInfoContainer
          onPress={() => {
            navigation.navigate('MyProfileDataPrivacySettings');
          }}
        >
          <StyledProfileInfoView testID={elementIDs.settingsPrivacyID}>
            <B color='dark'>Data & Privacy</B>
            <Spacer size={1} />
            <Caption color='dark'>Settings</Caption>
          </StyledProfileInfoView>
          <Octicons name='chevron-right' size={24} color='black' />
        </StyledProfileInfoContainer>
        <Spacer size={3} />
        <StyledButton
          testID={elementIDs.settingsLogOutID}
          color='#FFE8DA'
          onPress={() => handleLogOut()}
        >
          Log Out
        </StyledButton>
        <StyledVersionBuildText color='light' padding={2}>
          v{AppManifest.version}
          {Platform.OS === 'ios' || Platform.OS === 'android'
            ? ` (build: ${AppManifest.buildNumber})`
            : ''}
        </StyledVersionBuildText>
      </StyledContainer>
    </StyledScrollView>
  );
};

export default MyProfileSettingsScreen;
